export interface UserFriendlyError {
  title: string;
  message: string;
  recoverySuggestions?: string[];
}

export const ERROR_MESSAGES: Record<string, UserFriendlyError> = {
  // Network errors
  NETWORK_ERROR: {
    title: 'Connection Problem',
    message: 'Unable to connect to the server. Please check your internet connection.',
    recoverySuggestions: ['Check your internet connection', 'Try refreshing the page', 'Wait a moment and try again'],
  },
  TIMEOUT_ERROR: {
    title: 'Request Timeout',
    message: 'The request took too long to complete.',
    recoverySuggestions: ['Try again', 'Check your internet connection'],
  },

  // Authentication errors
  UNAUTHORIZED: {
    title: 'Authentication Required',
    message: 'You need to sign in to access this feature.',
    recoverySuggestions: ['Sign in to your account', 'Check if your session has expired'],
  },
  FORBIDDEN: {
    title: 'Access Denied',
    message: "You don't have permission to access this resource.",
    recoverySuggestions: ['Contact your administrator', 'Verify your account permissions'],
  },
  SESSION_EXPIRED: {
    title: 'Session Expired',
    message: 'Your session has expired. Please sign in again.',
    recoverySuggestions: ['Sign in again to continue'],
  },

  // Resource errors
  NOT_FOUND: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    recoverySuggestions: ['Check the URL', 'Go back to the previous page', 'Return to dashboard'],
  },

  // Server errors
  SERVER_ERROR: {
    title: 'Server Error',
    message: 'Something went wrong on our end. Please try again later.',
    recoverySuggestions: ['Try again in a few moments', 'Refresh the page', 'Contact support if the problem persists'],
  },
  SERVICE_UNAVAILABLE: {
    title: 'Service Unavailable',
    message: 'The service is temporarily unavailable. Please try again later.',
    recoverySuggestions: ['Wait a few minutes and try again', 'Check our status page'],
  },

  // Validation errors
  VALIDATION_ERROR: {
    title: 'Invalid Input',
    message: 'Please check your input and try again.',
    recoverySuggestions: ['Review the form for errors', 'Ensure all required fields are filled'],
  },
  INVALID_INVITE_CODE: {
    title: 'Invalid Invite Code',
    message: 'The invite code you entered is invalid or has expired.',
    recoverySuggestions: ['Check the invite code for typos', 'Request a new invite code'],
  },
  INVALID_PHONE_NUMBER: {
    title: 'Invalid Phone Number',
    message: 'Please enter a valid phone number.',
    recoverySuggestions: ['Use the format: (555) 555-5555', 'Include your area code'],
  },

  // Payment errors
  PAYMENT_FAILED: {
    title: 'Payment Failed',
    message: 'Your payment could not be processed.',
    recoverySuggestions: ['Check your payment information', 'Try a different payment method', 'Contact your bank'],
  },
  PAYMENT_REQUIRED: {
    title: 'Payment Required',
    message: 'You need to complete payment to access this feature.',
    recoverySuggestions: ['Complete your payment', 'Contact support for assistance'],
  },

  // Goal tracking errors
  GOAL_CREATION_FAILED: {
    title: 'Goal Creation Failed',
    message: 'Unable to create your goal. Please try again.',
    recoverySuggestions: ['Check your goal details', 'Ensure all required fields are filled'],
  },
  ACTIVITY_TRACKING_FAILED: {
    title: 'Activity Tracking Failed',
    message: 'Unable to record your activity. Please try again.',
    recoverySuggestions: ['Try again', 'Refresh the page'],
  },

  // Survey errors
  SURVEY_SUBMISSION_FAILED: {
    title: 'Survey Submission Failed',
    message: 'Unable to submit your survey responses.',
    recoverySuggestions: ['Check your answers', 'Try submitting again'],
  },

  // Generic fallback
  UNKNOWN_ERROR: {
    title: 'Something Went Wrong',
    message: 'An unexpected error occurred.',
    recoverySuggestions: ['Try again', 'Refresh the page', 'Contact support if the problem persists'],
  },
};

export function getErrorMessage(statusCode: number, errorType?: string): UserFriendlyError {
  // Check for specific error type first
  if (errorType && ERROR_MESSAGES[errorType]) {
    return ERROR_MESSAGES[errorType];
  }

  // Map status codes to error messages
  switch (statusCode) {
    case 400:
      return ERROR_MESSAGES.VALIDATION_ERROR;
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED;
    case 403:
      return ERROR_MESSAGES.FORBIDDEN;
    case 404:
      return ERROR_MESSAGES.NOT_FOUND;
    case 408:
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    case 429:
      return {
        title: 'Too Many Requests',
        message: 'You have made too many requests. Please wait a moment and try again.',
        recoverySuggestions: ['Wait a few moments before trying again'],
      };
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_MESSAGES.SERVER_ERROR;
    default:
      return ERROR_MESSAGES.UNKNOWN_ERROR;
  }
}

export function formatErrorForUser(error: unknown, context?: string): UserFriendlyError {
  // Handle Error objects
  if (error instanceof Error) {
    // Check for specific error messages
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return ERROR_MESSAGES.NETWORK_ERROR;
    }
    if (error.message.includes('timeout')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }
    if (error.message.includes('unauthorized') || error.message.includes('Authorization failed')) {
      return ERROR_MESSAGES.UNAUTHORIZED;
    }
  }

  // Handle objects with status codes
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const statusCode = (error as any).status;
    return getErrorMessage(statusCode);
  }

  // Context-specific errors
  if (context) {
    switch (context) {
      case 'goal':
        return ERROR_MESSAGES.GOAL_CREATION_FAILED;
      case 'activity':
        return ERROR_MESSAGES.ACTIVITY_TRACKING_FAILED;
      case 'survey':
        return ERROR_MESSAGES.SURVEY_SUBMISSION_FAILED;
      case 'payment':
        return ERROR_MESSAGES.PAYMENT_FAILED;
    }
  }

  // Default fallback
  return ERROR_MESSAGES.UNKNOWN_ERROR;
}
